import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Select from "react-select";
import { checkboxMultiProps } from "./utils/MultiSelectCheckbox";
import { FaPlus, FaEdit, FaTrash, FaTimes, FaGripVertical, FaArrowsAlt, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import logo_eagle from "./assets/img/logo_eagle.png";
import { apiFetch, API_BASE } from "./utils/api";
import "./Dolluzcorp_Banner_config.css";

// react-select renders its menu inline by default, so any ancestor with
// overflow:hidden (our form cards) clips it. Portal the menu to <body>.
const SELECT_PORTAL = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
};

// ── Banner types + audience buckets (kept in sync with the backend) ──────────
const BANNER_TYPES = [
    { value: "image", label: "Image only", hint: "Just a banner image." },
    { value: "buttons", label: "Buttons", hint: "Image + call-to-action buttons that link out." },
    { value: "poll", label: "Poll", hint: "Image + a question with options people vote on." },
    { value: "yesno", label: "Yes / No", hint: "Image + a question answered Yes or No." },
];

const AUDIENCE_TYPES = [
    { value: "general", label: "General - Everyone (public)" },
    { value: "all_employees", label: "All Employees (logged-in staff)" },
    { value: "department", label: "Department" },
    { value: "job_position", label: "Job Position" },
    { value: "location", label: "Location" },
    { value: "access_level", label: "Access Level" },
    { value: "employee", label: "Employee" },
];
const SCOPED = ["department", "job_position", "location", "access_level", "employee"];
const LOCATIONS = ["Chennai", "Hyderabad", "Pune", "Nagpur", "Mumbai"];
const ACCESS_LEVELS = ["Admin", "Sub Admin", "Manager", "User"];

// EVERY banner renders in one fixed box - 1600/780 (80:39, 2.0513:1). This
// mirrors the portal exactly (its --bn-ratio token); the ratio is fixed so the
// hero never changes height as the carousel advances, and the image fills the
// box with object-fit: cover. Cropping the edges is the accepted trade-off.
//
// The box shape MUST match the portal, because overlays are stored as a % of
// it - a different shape here and the placement preview lies to the author.
const BANNER_RATIO = "1600 / 780";

// The box is fixed, so an image that is not 2.05:1 has to give somewhere:
//   cover   - fill the box, crop the overflow (default; no empty space)
//   contain - show the WHOLE image, letterboxed inside the box
// Stored per banner so the author picks per image, and the portal honours it.
const IMAGE_FITS = [
    { value: "cover", label: "Fill", hint: "Fills the banner. Cuts off the top and bottom." },
    { value: "contain", label: "Fit", hint: "Shows the whole image. Leaves space at the sides." },
];
const safeFit = (v) => (v === "contain" ? "contain" : "cover");
const DEFAULT_FRAME_W = 1600;
const DEFAULT_FRAME_H = 780;
const EMPTY_FORM = {
    banner_type: "image",
    title: "",
    audience_type: "general",
    audience_value: [],
    buttons: [],                                   // [{label,url,pos_x,pos_y}]
    options: [{ option_text: "" }, { option_text: "" }],
    options_layout: "horizontal",                  // poll / yes-no option arrangement
    image_fit: "cover",                            // cover = fill & crop | contain = whole image, letterboxed
    overlay_x: 50, overlay_y: 50,                  // poll / yes-no widget position
    // Kept only so the columns stay populated; layout no longer reads them.
    frame_width: DEFAULT_FRAME_W,
    frame_height: DEFAULT_FRAME_H,
    imageFile: null, imagePreview: null, existingImage: null,
};

// One box for every banner. Width fills the column; aspect-ratio fixes the
// height, so the box is responsive but its SHAPE never varies.
const frameBoxStyle = () => ({ width: "100%", aspectRatio: BANNER_RATIO });
// Snapshot of a banner as it was when Edit was opened - used to warn before a
// save discards votes (type switch, or a removed poll option).
const EMPTY_ORIGINAL = { banner_type: null, voteCount: 0, options: [] };

const clampPct = (n) => Math.min(100, Math.max(0, n));

// A button link must be a real http(s) URL - anything else would break on the portal.
const isHttpUrl = (u) => {
    try { const x = new URL(u); return x.protocol === "http:" || x.protocol === "https:"; }
    catch { return false; }
};

const Dolluzcorp_Banner_config = ({ navSize }) => {
    const navigate = useNavigate();
    const [banners, setBanners] = useState([]);
    const [orderChanged, setOrderChanged] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);   // live portal-preview carousel
    const [previewVotes, setPreviewVotes] = useState({});  // { banner_id: option_id } - preview only, never saved

    const [departments, setDepartments] = useState([]);
    const [jobPositions, setJobPositions] = useState([]);
    const [employees, setEmployees] = useState([]);

    const [editId, setEditId] = useState(null);
    const [editOriginal, setEditOriginal] = useState(EMPTY_ORIGINAL);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [saving, setSaving] = useState(false);
    // Responses drill-down: { banner, optionId, rows, loading } | null
    const [votes, setVotes] = useState(null);
    const fileInputRef = useRef(null);

    // WYSIWYG placement drag state
    const canvasRef = useRef(null);
    const [drag, setDrag] = useState(null);        // { kind: 'button'|'poll', index? } | null

    useEffect(() => {
        verifySession();
        fetchBanners();
        fetchDepartments();
        fetchJobPositions();
        fetchEmployees();
    }, []);

    // Auto-advance the live portal preview, like the real carousel on the site.
    useEffect(() => {
        if (banners.length <= 1) return;
        const id = setInterval(() => setPreviewIndex(i => (i + 1) % banners.length), 4500);
        return () => clearInterval(id);
    }, [banners.length]);

    // Drag-to-position on the image canvas (positions stored as % so they scale).
    useEffect(() => {
        if (!drag) return;
        const onMove = (e) => {
            const rect = canvasRef.current?.getBoundingClientRect();
            if (!rect) return;
            const cx = e.touches ? e.touches[0].clientX : e.clientX;
            const cy = e.touches ? e.touches[0].clientY : e.clientY;
            const x = clampPct(((cx - rect.left) / rect.width) * 100);
            const y = clampPct(((cy - rect.top) / rect.height) * 100);
            if (drag.kind === "button") {
                setForm(prev => {
                    const buttons = [...prev.buttons];
                    buttons[drag.index] = { ...buttons[drag.index], pos_x: x, pos_y: y };
                    return { ...prev, buttons };
                });
            } else {
                setForm(prev => ({ ...prev, overlay_x: x, overlay_y: y }));
            }
        };
        const onUp = () => setDrag(null);
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchmove", onMove, { passive: false });
        window.addEventListener("touchend", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onUp);
        };
    }, [drag]);

    // Drill into who responded. optionId narrows to one option; null shows all.
    const openVotes = async (banner, optionId) => {
        setVotes({ banner, optionId, rows: [], loading: true });
        try {
            const r = await apiFetch(`/api/Dolluzcorp/banner/votes/${banner.banner_id}`);
            const d = await r.json();
            if (!d.success) throw new Error(d.message || "Failed");
            setVotes({ banner, optionId, rows: d.data || [], loading: false });
        } catch (e) {
            setVotes(null);
            dnAlert.error("Couldn’t load responses", "Something went wrong. Please try again.");
        }
    };

    const verifySession = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, { method: "GET", credentials: "include" });
            const data = await res.json();
            if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") navigate("/login");
        } catch (err) { console.error("session check failed:", err); }
    };

    const fetchBanners = async () => {
        try {
            const res = await apiFetch("/api/Dolluzcorp/banner/list");
            const data = await res.json();
            if (data.success) {
                setBanners((data.data || []).sort((a, b) => a.display_order - b.display_order));
                setOrderChanged(false);
            }
        } catch (err) { console.error(err); }
    };

    const fetchDepartments = async () => { try { const r = await apiFetch("/api/Employee_configuration/get-department_config"); setDepartments(await r.json()); } catch { /* ignore */ } };
    const fetchJobPositions = async () => { try { const r = await apiFetch("/api/Employee_configuration/get-job_position_config"); setJobPositions(await r.json()); } catch { /* ignore */ } };
    const fetchEmployees = async () => { try { const r = await apiFetch("/api/employee/directory"); setEmployees(await r.json()); } catch { /* ignore */ } };

    const audienceOptionsFor = (type) => {
        switch (type) {
            case "department": return departments.map(d => ({ value: String(d.department_id), label: d.department_name }));
            case "job_position": return jobPositions.map(j => ({ value: String(j.job_id), label: j.job_name }));
            case "location": return LOCATIONS.map(l => ({ value: l, label: l }));
            case "access_level": return ACCESS_LEVELS.map(l => ({ value: l, label: l }));
            case "employee": return employees.map(e => ({ value: String(e.emp_id), label: `${e.emp_first_name} ${e.emp_last_name}` }));
            default: return [];
        }
    };

    const audienceBadge = (b) => {
        if (b.audience_type === "general") return "Everyone";
        if (b.audience_type === "all_employees") return "All Employees";
        const count = (b.audience_value || "").split(",").filter(Boolean).length;
        const label = { department: "Department", job_position: "Job Position", location: "Location", access_level: "Access Level", employee: "Employee" }[b.audience_type] || b.audience_type;
        return `${label}${count ? ` (${count})` : ""}`;
    };

    // ── open create / edit ───────────────────────────────────────────────────
    // Reset the inline compose form back to a blank "New Banner".
    const resetForm = () => {
        if (form.imagePreview) URL.revokeObjectURL(form.imagePreview);
        setEditId(null);
        setEditOriginal(EMPTY_ORIGINAL);
        setDrag(null);
        setForm({ ...EMPTY_FORM, buttons: [], options: [{ option_text: "" }, { option_text: "" }], audience_value: [] });
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const openEdit = (b) => {
        setEditId(b.banner_id);
        // Snapshot what the banner looked like before editing, so the form can
        // warn about changes that destroy votes (switching type, dropping an
        // option) instead of silently discarding them on save.
        setEditOriginal({
            banner_type: b.banner_type,
            voteCount: (b.options || []).reduce((n, o) => n + (Number(o.vote_count) || 0), 0),
            options: (b.options || []).map(o => ({ option_id: o.option_id, vote_count: Number(o.vote_count) || 0 })),
        });
        setForm({
            banner_type: b.banner_type,
            title: b.title || "",
            audience_type: b.audience_type,
            audience_value: (b.audience_value || "").split(",").filter(Boolean),
            buttons: b.buttons?.map(x => ({ label: x.label, url: x.url, bg_color: x.bg_color || "#e8520a", text_color: x.text_color || "#ffffff", pos_x: Number(x.pos_x), pos_y: Number(x.pos_y) })) || [],
            options: b.options?.length ? b.options.map(x => ({ option_id: x.option_id, option_text: x.option_text, vote_count: Number(x.vote_count) || 0 })) : [{ option_text: "" }, { option_text: "" }],
            options_layout: b.options_layout || "horizontal",
            image_fit: safeFit(b.image_fit),
            overlay_x: Number(b.overlay_x ?? 50), overlay_y: Number(b.overlay_y ?? 50),
            frame_width: Number(b.frame_width) || DEFAULT_FRAME_W,
            frame_height: Number(b.frame_height) || DEFAULT_FRAME_H,
            imageFile: null, imagePreview: null, existingImage: b.banner_image,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const patch = (p) => setForm(prev => ({ ...prev, ...p }));

    const onPickImage = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (form.imagePreview) URL.revokeObjectURL(form.imagePreview);
        const url = URL.createObjectURL(f);
        patch({ imageFile: f, imagePreview: url });
        e.target.value = "";

        // Nothing is cropped now - so default the banner box to the image's own
        // shape (fitted into the allowed range). The whole image then fills the
        // Every banner renders in ONE fixed 2.05:1 box and the image is
        // cropped to fill it, so warn when an image is far from that shape -
        // a tall image loses a lot of its top and bottom.
        const probe = new Image();
        probe.onload = () => {
            // Only a problem on Fill, where the image is cropped to the box.
            // On Fit the whole image is shown, so there is nothing to warn about.
            const isSquarish = probe.naturalWidth / probe.naturalHeight < 1.4;
            if (isSquarish && form.image_fit === "cover") {
                dnAlert.warning(
                    "Top and bottom will be cut off",
                    "This image is quite square, and Fill crops it to the banner shape. Choose Fit to show the whole image."
                );
            }
        };
        probe.src = url;
    };

    const setButton = (i, k, v) => setForm(prev => { const buttons = [...prev.buttons]; buttons[i] = { ...buttons[i], [k]: v }; return { ...prev, buttons }; });
    const addButton = () => setForm(prev => ({ ...prev, buttons: [...prev.buttons, { label: "New button", url: "", bg_color: "#e8520a", text_color: "#ffffff", pos_x: 50, pos_y: 85 }] }));
    const removeButton = (i) => setForm(prev => ({ ...prev, buttons: prev.buttons.filter((_, idx) => idx !== i) }));

    const setOption = (i, v) => setForm(prev => { const options = [...prev.options]; options[i] = { ...options[i], option_text: v }; return { ...prev, options }; });
    const addOption = () => setForm(prev => ({ ...prev, options: [...prev.options, { option_text: "" }] }));
    const removeOption = (i) => setForm(prev => ({ ...prev, options: prev.options.filter((_, idx) => idx !== i) }));

    // options as they render in the editor preview (yes/no is fixed)
    const editorOptions = () => form.banner_type === "yesno"
        ? [{ option_text: "Yes" }, { option_text: "No" }]
        : form.options.filter(o => o.option_text.trim() || form.banner_type === "poll");

    // ── save ─────────────────────────────────────────────────────────────────
    const validate = () => {
        const t = form.banner_type;
        if (!editId && !form.imageFile) return "Please choose a banner image.";

        if ((t === "poll" || t === "yesno") && !form.title.trim())
            return "Enter the question for this banner.";

        if (t === "buttons") {
            if (form.buttons.length === 0) return "Add at least one button.";
            // Every button must be complete - no silent dropping of half-filled rows.
            for (let i = 0; i < form.buttons.length; i++) {
                const label = (form.buttons[i].label || "").trim();
                const url = (form.buttons[i].url || "").trim();
                const n = `Button ${i + 1}`;
                if (!label && !url) return `${n} is empty - fill it in or remove it.`;
                if (!label) return `${n} needs a label.`;
                if (!url) return `“${label}” needs a link.`;
                if (!isHttpUrl(url)) return `“${label}” has an invalid link - it must start with http:// or https://`;
            }
        }

        if (t === "poll") {
            const filled = form.options.map(o => (o.option_text || "").trim()).filter(Boolean);
            if (filled.length < 2) return "A poll needs at least two options.";
            const lower = filled.map(s => s.toLowerCase());
            if (new Set(lower).size !== lower.length) return "Poll options must be unique - please remove duplicates.";
        }

        if (SCOPED.includes(form.audience_type) && form.audience_value.length === 0)
            return "Pick at least one value for the selected audience.";

        return null;
    };

    const handleSave = async () => {
        const err = validate();
        if (err) { dnAlert.warning("Check the form", err); return; }

        const fd = new FormData();
        if (form.imageFile) fd.append("image", form.imageFile);
        fd.append("banner_type", form.banner_type);
        fd.append("title", form.title.trim());
        fd.append("audience_type", form.audience_type);
        fd.append("audience_value", SCOPED.includes(form.audience_type) ? form.audience_value.join(",") : "");
        fd.append("overlay_x", form.overlay_x);
        fd.append("overlay_y", form.overlay_y);
        fd.append("frame_width", form.frame_width);
        fd.append("frame_height", form.frame_height);
        fd.append("options_layout", form.options_layout);
        fd.append("image_fit", form.image_fit);
        if (form.banner_type === "buttons")
            fd.append("buttons", JSON.stringify(form.buttons.filter(b => b.label.trim() && b.url.trim())));
        // Send every option (kept ones carry option_id, new ones don't). The
        // server reconciles by id, so omitted options are the removed ones.
        if (form.banner_type === "poll")
            fd.append("options", JSON.stringify(form.options.filter(o => o.option_text.trim())));

        // Warn before a save that destroys collected votes - it can't be undone.
        const typeSwitchLoses = editId
            && form.banner_type !== editOriginal.banner_type
            && editOriginal.voteCount > 0;
        const losing = typeSwitchLoses ? editOriginal.voteCount : removedVoteCount;
        if (losing > 0) {
            const ok = await dnAlert.confirm({
                title: `Delete ${losing} vote${losing === 1 ? "" : "s"}?`,
                message: typeSwitchLoses
                    ? `Changing the type from ${typeLabel(editOriginal.banner_type)} removes its options and the ${losing} vote${losing === 1 ? "" : "s"} already cast. This can't be undone.`
                    : `The option${losing === 1 ? "" : "s"} you removed already had ${losing} vote${losing === 1 ? "" : "s"}. Saving deletes them permanently.`,
                danger: true,
                confirmLabel: "Save anyway",
            });
            if (!ok) return;
        }

        setSaving(true);
        try {
            const url = editId ? `/api/Dolluzcorp/banner/update/${editId}` : "/api/Dolluzcorp/banner/create";
            const res = await apiFetch(url, { method: "POST", body: fd });
            const data = await res.json();
            if (!data.success) { if (data.devError) console.error("banner save:", data.devError); dnAlert.error("Couldn’t save", data.message || "Something went wrong. Please try again."); return; }
            dnAlert.success(editId ? "Banner updated" : "Banner created", "Saved successfully.");
            resetForm();
            fetchBanners();
        } catch (e) { dnAlert.error("Server error", e.message); }
        finally { setSaving(false); }
    };

    const deleteBanner = async (banner_id) => {
        const ok = await dnAlert.confirm({
            title: "Delete banner?",
            message: "The image and any buttons, poll options and votes will be permanently removed.",
            danger: true, confirmLabel: "Delete",
        });
        if (!ok) return;
        const res = await apiFetch("/api/Dolluzcorp/banner/delete", {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ banner_id }),
        });
        const data = await res.json();
        if (!data.success) { dnAlert.error("Error", data.message || "Delete failed"); return; }
        dnAlert.success("Deleted", "Banner removed");
        // If the banner being edited is the one just deleted, clear the form.
        // Leaving it populated offers "Save changes" on a row that no longer
        // exists - the save would fail, or worse, look like it worked.
        if (editId === banner_id) resetForm();
        fetchBanners();
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(banners);
        const [moved] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, moved);
        setBanners(items.map((b, i) => ({ ...b, display_order: i + 1 })));
        setOrderChanged(true);
    };

    const saveOrder = async () => {
        const payload = banners.map(b => ({ banner_id: b.banner_id, display_order: b.display_order }));
        const res = await apiFetch("/api/Dolluzcorp/banner/update-order", {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!data.success) { dnAlert.error("Update failed", data.message); return; }
        dnAlert.success("Saved", "Banner order updated");
        setOrderChanged(false);
    };

    const typeLabel = (t) => BANNER_TYPES.find(x => x.value === t)?.label || t;
    // Votes that a save would destroy: options present before the edit that are
    // no longer in the form. Deleting a banner_options row cascades its votes.
    const removedVoteCount = (() => {
        if (!editId || form.banner_type !== "poll" || editOriginal.banner_type !== "poll") return 0;
        const keptIds = new Set(form.options.map(o => o.option_id).filter(Boolean));
        return editOriginal.options
            .filter(o => !keptIds.has(o.option_id))
            .reduce((n, o) => n + o.vote_count, 0);
    })();

    const isScoped = SCOPED.includes(form.audience_type);
    const previewIdx = banners.length ? Math.min(previewIndex, banners.length - 1) : 0;

    // ── overlay preview (shared by editor canvas, list cards, live carousel) ───
    //   editable    - draggable handles (the modal editor)
    //   interactive - real behaviour: buttons open links, poll options are
    //                 clickable (highlight only, nothing is saved)
    //   otherwise   - a plain static preview (the cards)
    const OverlayLayer = ({ type, title, buttons, options, layout, ox, oy, editable, interactive, votedId, onPick }) => (
        <>
            {type === "buttons" && buttons?.map((b, i) => {
                const style = { left: `${b.pos_x}%`, top: `${b.pos_y}%`, background: b.bg_color || "#e8520a", color: b.text_color || "#ffffff" };
                if (interactive) {
                    return (
                        <a key={i} className="bn-ov-btn interactive" style={style} href={b.url || "#"} target="_blank" rel="noopener noreferrer">
                            {b.label?.trim() || "Button"}
                        </a>
                    );
                }
                return (
                    <div
                        key={i}
                        className={`bn-ov-btn ${editable ? "editable" : ""}`}
                        style={style}
                        onMouseDown={editable ? () => setDrag({ kind: "button", index: i }) : undefined}
                        onTouchStart={editable ? () => setDrag({ kind: "button", index: i }) : undefined}
                    >
                        {editable && <FaArrowsAlt className="bn-ov-grip" size={9} />}
                        {b.label?.trim() || "Button"}
                    </div>
                );
            })}

            {(type === "poll" || type === "yesno") && (
                <div
                    className={`bn-ov-poll ${editable ? "editable" : ""}`}
                    style={{ left: `${ox}%`, top: `${oy}%` }}
                    onMouseDown={editable ? () => setDrag({ kind: "poll" }) : undefined}
                    onTouchStart={editable ? () => setDrag({ kind: "poll" }) : undefined}
                >
                    {editable && <span className="bn-ov-poll-drag"><FaArrowsAlt size={10} /> drag</span>}
                    <div className="bn-ov-poll-q">{title?.trim() || "Your question here"}</div>
                    <div className={`bn-ov-poll-opts ${layout === "vertical" ? "vertical" : "horizontal"}`}>
                        {options?.map((o, i) => (
                            interactive ? (
                                <button
                                    key={o.option_id ?? i}
                                    type="button"
                                    className={`bn-ov-poll-opt clickable ${votedId === o.option_id ? "picked" : ""}`}
                                    onClick={() => onPick && onPick(o.option_id)}
                                >
                                    {votedId === o.option_id ? "✓ " : ""}{o.option_text || `Option ${i + 1}`}
                                </button>
                            ) : (
                                <span key={o.option_id ?? i} className="bn-ov-poll-opt">{o.option_text || `Option ${i + 1}`}</span>
                            )
                        ))}
                    </div>
                    {interactive && votedId && <div className="bn-ov-poll-thanks">✓ Thanks for voting! (preview - not saved)</div>}
                </div>
            )}
        </>
    );

    // Live portal carousel - one banner at a time at its natural size (matches
    // the editor exactly), buttons/polls interactive. Rendered at the bottom.
    const current = banners[previewIdx];
    const portalPreview = banners.length > 0 && current && (
        <div className="bn-portal">
            <div className="bn-portal-label">
                <span className="bn-portal-live" /> Live portal preview - how it appears on inside.dolluzcorp.com
                <span className="bn-portal-hint">buttons &amp; polls work here · nothing is saved</span>
            </div>
            <div className="bn-portal-viewport">
                <div className="bn-portal-frame" key={current.banner_id} style={frameBoxStyle()}>
                    <img src={`${API_BASE}/${current.banner_image}`} alt="banner" style={{ objectFit: safeFit(current.image_fit) }} />
                    <OverlayLayer
                        type={current.banner_type} title={current.title}
                        buttons={current.buttons} options={current.options} layout={current.options_layout}
                        ox={current.overlay_x} oy={current.overlay_y}
                        editable={false} interactive={true}
                        votedId={previewVotes[current.banner_id]}
                        onPick={(oid) => setPreviewVotes(v => ({ ...v, [current.banner_id]: oid }))}
                    />
                </div>
                {banners.length > 1 && (
                    <>
                        <button className="bn-portal-nav prev" onClick={() => setPreviewIndex((previewIdx - 1 + banners.length) % banners.length)} aria-label="Previous"><FaChevronLeft /></button>
                        <button className="bn-portal-nav next" onClick={() => setPreviewIndex((previewIdx + 1) % banners.length)} aria-label="Next"><FaChevronRight /></button>
                        <div className="bn-portal-dots">
                            {banners.map((b, i) => (
                                <button key={b.banner_id} className={`bn-portal-dot ${i === previewIdx ? "active" : ""}`} onClick={() => setPreviewIndex(i)} aria-label={`Banner ${i + 1}`} />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <div className="banner-container">
            <div className={`banner-dashboard ${navSize}`}>
                <div className="banner-header-wrapper">
                    <h2>
                        <img src={logo_eagle} alt="dAdmin Logo" className="banner-header-logo" />
                        Banner Configuration
                    </h2>
                    <p className="banner-subtitle">
                        Manage the banners shown on the Inside D portal - plain images, image + buttons, polls, or Yes/No.
                        Place each element right on the image, set who it's for, reorder, and watch poll results live.
                    </p>
                </div>

                <div className="banner-dashboard-content">
                    {/* TOP: left compose form + right live drag canvas */}
                    <div className="bn-grid-top">
                        <div className="bn-compose">
                            <div className="bn-form-card">
                                <div className="bn-form-head">
                                    <h4>{editId ? "Edit Banner" : "New Banner"}</h4>
                                    {editId && <span className="bn-edit-badge">EDIT</span>}
                                </div>
                                <div className="bn-form">
                                    <label className="bn-label">Banner Image{!editId && <span className="bn-req"> *</span>} {editId && <span className="bn-hint">(leave blank to keep current)</span>}</label>
                                    <div className="bn-upload">
                                        <input type="file" accept="image/*" ref={fileInputRef} onChange={onPickImage} />
                                    </div>
                                    <div className="bn-hint bn-upload-hint">
                                        All banners are the same size. Wide images (about 1600×780) work best.
                                    </div>

                                    {/* Per-banner box size. The image is shown WHOLE inside this box
                                        (no crop); overlays are placed as a % of the box. */}

                                    <label className="bn-label">Image Fit</label>
                                    <div className="bn-type-seg">
                                        {IMAGE_FITS.map(f => (
                                            <button key={f.value} type="button" title={f.hint}
                                                className={`bn-type-opt ${form.image_fit === f.value ? "active" : ""}`}
                                                onClick={() => patch({ image_fit: f.value })}>
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="bn-hint bn-upload-hint">
                                        {form.image_fit === "contain"
                                            ? "Whole image shown. Space at the sides."
                                            : "No empty space. Top and bottom may be cut off."}
                                    </div>

                                    <label className="bn-label">Banner Type</label>
                                    <div className="bn-type-seg">
                                        {BANNER_TYPES.map(t => (
                                            <button key={t.value} type="button" title={t.hint}
                                                className={`bn-type-opt ${form.banner_type === t.value ? "active" : ""}`}
                                                onClick={() => patch({ banner_type: t.value })}>
                                                {t.label}
                                            </button>
                                        ))}
                                    </div>
                                    {editId && form.banner_type !== editOriginal.banner_type && (
                                        <p className="bn-warn">
                                            Changing the type from <strong>{typeLabel(editOriginal.banner_type)}</strong> discards
                                            that type&rsquo;s content
                                            {editOriginal.voteCount > 0 && <> - including <strong>{editOriginal.voteCount} vote{editOriginal.voteCount === 1 ? "" : "s"}</strong></>}.
                                        </p>
                                    )}

                                    <label className="bn-label">
                                        {form.banner_type === "poll" || form.banner_type === "yesno" ? "Question" : "Caption"}
                                        {(form.banner_type === "poll" || form.banner_type === "yesno") && <span className="bn-req"> *</span>}
                                        {(form.banner_type === "image" || form.banner_type === "buttons") && <span className="bn-hint"> (optional)</span>}
                                    </label>
                                    <input className="bn-input" value={form.title} onChange={(e) => patch({ title: e.target.value })} maxLength={255}
                                        placeholder={form.banner_type === "poll" || form.banner_type === "yesno" ? "e.g. Which lunch option do you prefer?" : "Short caption (optional)"} />

                                    {form.banner_type === "buttons" && (
                                        <div className="bn-sub">
                                            <label className="bn-label">Buttons<span className="bn-req"> *</span> <span className="bn-hint">(drag each one on the preview to position it)</span></label>
                                            {form.buttons.map((btn, i) => (
                                                <div className="bn-btn-edit" key={i}>
                                                    <div className="bn-row">
                                                        <input className="bn-input" placeholder="Button label" value={btn.label} onChange={(e) => setButton(i, "label", e.target.value)} maxLength={100} />
                                                        <input className="bn-input" placeholder="https://link…" value={btn.url} onChange={(e) => setButton(i, "url", e.target.value)} maxLength={500} />
                                                        <button type="button" className="bn-row-del" onClick={() => removeButton(i)} title="Remove"><FaTimes /></button>
                                                    </div>
                                                    <div className="bn-color-row">
                                                        <label className="bn-color-lbl">Fill<input type="color" value={btn.bg_color} onChange={(e) => setButton(i, "bg_color", e.target.value)} /></label>
                                                        <label className="bn-color-lbl">Text<input type="color" value={btn.text_color} onChange={(e) => setButton(i, "text_color", e.target.value)} /></label>
                                                        <span className="bn-color-preview" style={{ background: btn.bg_color, color: btn.text_color }}>{btn.label?.trim() || "Preview"}</span>
                                                    </div>
                                                </div>
                                            ))}
                                            <button type="button" className="bn-row-add" onClick={addButton}><FaPlus size={10} /> Add button</button>
                                        </div>
                                    )}

                                    {form.banner_type === "poll" && (
                                        <div className="bn-sub">
                                            <label className="bn-label">Options<span className="bn-req"> *</span></label>
                                            {form.options.map((o, i) => (
                                                <div className="bn-row" key={o.option_id ?? `new-${i}`}>
                                                    <input className="bn-input" placeholder={`Option ${i + 1}`} value={o.option_text} onChange={(e) => setOption(i, e.target.value)} maxLength={150} />
                                                    {o.vote_count > 0 && (
                                                        <span className="bn-opt-votes" title="Votes cast for this option - removing it deletes them">
                                                            {o.vote_count} vote{o.vote_count === 1 ? "" : "s"}
                                                        </span>
                                                    )}
                                                    {form.options.length > 2 && (
                                                        <button type="button" className="bn-row-del" onClick={() => removeOption(i)} title="Remove"><FaTimes /></button>
                                                    )}
                                                </div>
                                            ))}
                                            <button type="button" className="bn-row-add" onClick={addOption}><FaPlus size={10} /> Add option</button>
                                            {editId && removedVoteCount > 0 && (
                                                <p className="bn-warn">
                                                    Saving will delete <strong>{removedVoteCount} vote{removedVoteCount === 1 ? "" : "s"}</strong> cast
                                                    for the option{removedVoteCount === 1 ? "" : "s"} you removed.
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {(form.banner_type === "poll" || form.banner_type === "yesno") && (
                                        <div className="bn-sub">
                                            <label className="bn-label">Options layout</label>
                                            <div className="bn-type-seg">
                                                <button type="button" className={`bn-type-opt ${form.options_layout === "horizontal" ? "active" : ""}`} onClick={() => patch({ options_layout: "horizontal" })}>Horizontal</button>
                                                <button type="button" className={`bn-type-opt ${form.options_layout === "vertical" ? "active" : ""}`} onClick={() => patch({ options_layout: "vertical" })}>Vertical</button>
                                            </div>
                                        </div>
                                    )}

                                    <label className="bn-label">Who sees this?<span className="bn-req"> *</span></label>
                                    <Select
                                        classNamePrefix="bn-select" {...SELECT_PORTAL}
                                        options={AUDIENCE_TYPES}
                                        value={AUDIENCE_TYPES.find(a => a.value === form.audience_type)}
                                        onChange={(sel) => patch({ audience_type: sel.value, audience_value: [] })}
                                    />
                                    {isScoped && (
                                        <div className="bn-sub">
                                            <label className="bn-label">Select {form.audience_type.replace("_", " ")}</label>
                                            <Select
                                                key={form.audience_type}
                                                isMulti
                                                {...checkboxMultiProps}
                                                classNamePrefix="bn-select" {...SELECT_PORTAL}
                                                options={audienceOptionsFor(form.audience_type)}
                                                value={audienceOptionsFor(form.audience_type).filter(o => form.audience_value.includes(String(o.value)))}
                                                onChange={(sel) => patch({ audience_value: (sel || []).map(s => String(s.value)) })}
                                            />
                                        </div>
                                    )}

                                    <div className="bn-form-actions">
                                        {editId && <button className="bn-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>}
                                        <button className="bn-btn-save" onClick={handleSave} disabled={saving}>
                                            {saving ? "Saving…" : editId ? "Save changes" : "Publish Banner"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bn-canvas-col">
                            <div className="bn-canvas-collabel">
                                <span className="bn-live" /> Live preview{form.banner_type !== "image" ? " - drag elements to place them" : ""}
                            </div>
                            {(form.imagePreview || form.existingImage) ? (
                                <div className="bn-canvas" ref={canvasRef} style={frameBoxStyle()}>
                                    <img src={form.imagePreview || `${API_BASE}/${form.existingImage}`} alt="banner" style={{ objectFit: form.image_fit }} />
                                    <OverlayLayer type={form.banner_type} title={form.title} buttons={form.buttons} options={editorOptions()} layout={form.options_layout} ox={form.overlay_x} oy={form.overlay_y} editable={true} />
                                </div>
                            ) : (
                                <div className="bn-canvas-empty">
                                    <div className="bn-canvas-empty-icon">🖼️</div>
                                    <p>Upload an image on the left to see the live preview.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* BELOW: published banners */}
                    <div className="bn-published">
                        <div className="bn-published-head">
                            Published Banners <span className="bn-count-pill">{banners.length}</span>
                            {orderChanged
                                ? <button className="bn-order-save" onClick={saveOrder}>Save order</button>
                                : (banners.length > 1 && <span className="bn-reorder-hint">drag the handle to reorder</span>)}
                        </div>

                    {banners.length === 0 ? (
                        <div className="banner-empty-state">
                            <div className="empty-icon">🖼️</div>
                            <h3>No banners yet</h3>
                            <p>Create your first one using the form above.</p>
                        </div>
                    ) : (
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="banner-cards">
                                {(provided) => (
                                    <div className="bn-grid" ref={provided.innerRef} {...provided.droppableProps}>
                                        {banners.map((b, i) => (
                                            <Draggable key={b.banner_id} draggableId={String(b.banner_id)} index={i}>
                                                {(prov) => (
                                                    <div className="bn-card" ref={prov.innerRef} {...prov.draggableProps}>
                                                        {/* WYSIWYG preview: image + overlays exactly where they'll show */}
                                                        <div className="bn-card-canvas" style={frameBoxStyle()}>
                                                            <img className="bn-card-img" src={`${API_BASE}/${b.banner_image}`} alt="banner" style={{ objectFit: safeFit(b.image_fit) }} />
                                                            <OverlayLayer
                                                                type={b.banner_type} title={b.title}
                                                                buttons={b.buttons} options={b.options} layout={b.options_layout}
                                                                ox={b.overlay_x} oy={b.overlay_y} editable={false}
                                                            />
                                                            <span className="bn-drag" title="Drag to reorder" {...prov.dragHandleProps}><FaGripVertical /></span>
                                                        </div>

                                                        <div className="bn-card-body">
                                                            {b.title && <div className="bn-card-title">{b.title}</div>}

                                                            {(b.banner_type === "poll" || b.banner_type === "yesno") && (
                                                                <div className="bn-poll">
                                                                    {b.options?.map(o => {
                                                                        const pct = b.total_votes ? Math.round((Number(o.vote_count) / b.total_votes) * 100) : 0;
                                                                        return (
                                                                            <div className="bn-poll-row" key={o.option_id}>
                                                                                <div className="bn-poll-top">
                                                                                    <span className="bn-poll-label">{o.option_text}</span>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="bn-poll-count as-link"
                                                                                        disabled={!Number(o.vote_count)}
                                                                                        title={Number(o.vote_count) ? "See who responded" : "No responses yet"}
                                                                                        onClick={() => openVotes(b, o.option_id)}
                                                                                    >
                                                                                        {o.vote_count} · {pct}%
                                                                                    </button>
                                                                                </div>
                                                                                <div className="bn-poll-bar"><div className="bn-poll-fill" style={{ width: `${pct}%` }} /></div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    <div className="bn-poll-total">
                                                                        <button
                                                                            type="button"
                                                                            className="bn-poll-total-btn"
                                                                            disabled={!b.total_votes}
                                                                            onClick={() => openVotes(b, null)}
                                                                        >
                                                                            {b.total_votes || 0} response{b.total_votes === 1 ? "" : "s"}
                                                                        </button>
                                                                        {b.total_votes > 0 && (
                                                                            <span className="bn-poll-split">
                                                                                {b.total_employee_votes || 0} employee · {b.total_visitor_votes || 0} visitor
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <div className="bn-card-foot">
                                                                <div className="bn-card-badges">
                                                                    <span className={`bn-badge bn-badge-${b.banner_type}`}>{typeLabel(b.banner_type)}</span>
                                                                    <span className="bn-badge bn-badge-aud">{audienceBadge(b)}</span>
                                                                </div>
                                                                <div className="bn-card-actions">
                                                                    <button className="bn-icon-btn" onClick={() => openEdit(b)} title="Edit"><FaEdit /></button>
                                                                    <button className="bn-icon-btn bn-icon-del" onClick={() => deleteBanner(b.banner_id)} title="Delete"><FaTrash /></button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}
                    </div>

                    {portalPreview}
                </div>
            </div>

            {/* ── Responses ─────────────────────────────────────────────────
                 Employees are named; visitors deliberately are not - we store
                 only an opaque per-browser key, never anything identifying. */}
            {votes && (() => {
                const rows = votes.optionId
                    ? votes.rows.filter(r => r.option_id === votes.optionId)
                    : votes.rows;
                const emps = rows.filter(r => r.is_employee);
                const visitors = rows.length - emps.length;
                const optName = votes.optionId
                    ? (votes.banner.options || []).find(o => o.option_id === votes.optionId)?.option_text
                    : null;
                return (
                    <div className="bn-votes-backdrop" onClick={() => setVotes(null)}>
                        <div className="bn-votes-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="bn-votes-head">
                                <div>
                                    <h4>{optName ? `Responses - ${optName}` : "All responses"}</h4>
                                    <p>{votes.banner.title || "Untitled banner"}</p>
                                </div>
                                <button className="bn-votes-close" onClick={() => setVotes(null)}><FaTimes /></button>
                            </div>

                            {votes.loading ? (
                                <div className="bn-votes-empty">Loading…</div>
                            ) : rows.length === 0 ? (
                                <div className="bn-votes-empty">No responses yet.</div>
                            ) : (
                                <>
                                    <div className="bn-votes-summary">
                                        <span className="bn-chip total"><strong>{rows.length}</strong> total</span>
                                        <span className="bn-chip emp"><strong>{emps.length}</strong> employee{emps.length === 1 ? "" : "s"}</span>
                                        <span className="bn-chip vis"><strong>{visitors}</strong> visitor{visitors === 1 ? "" : "s"}</span>
                                    </div>

                                    {/* One table for everyone. Visitors get the same row and
                                        timestamp as employees - they just stay unnamed, since
                                        all we hold for them is an opaque per-browser key. */}
                                    <table className="bn-votes-table">
                                        <thead>
                                            <tr>
                                                <th>Who</th><th>ID</th><th>Department</th>
                                                {!votes.optionId && <th>Answer</th>}
                                                <th>When</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map(r => (
                                                <tr key={r.vote_id} className={r.is_employee ? "is-emp" : "is-vis"}>
                                                    <td>
                                                        {r.is_employee
                                                            ? <><span className="bn-who emp">Employee</span> {r.emp_name}</>
                                                            : <><span className="bn-who vis">Visitor</span> <span className="bn-anon">not signed in</span></>}
                                                    </td>
                                                    <td className="bn-votes-mono">{r.emp_id || "-"}</td>
                                                    <td>{r.department_name || "-"}</td>
                                                    {!votes.optionId && <td>{r.option_text}</td>}
                                                    <td>{new Date(r.created_time).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {visitors > 0 && (
                                        <p className="bn-votes-note">
                                            Visitors are counted and timestamped, but never named - no personal
                                            data is collected for them.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default Dolluzcorp_Banner_config;
